import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";

const ROTATING = [
    "de la bonne humeur",
    "des conseils sur mesure",
    "des montures qui claquent",
    "de l'origine France",
];

export default function Hero() {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const id = setInterval(() => {
            setIndex((i) => (i + 1) % ROTATING.length);
        }, 2600);
        return () => clearInterval(id);
    }, []);

    return (
        <section
            id="hero"
            className="relative pt-20 md:pt-24 pb-0 overflow-hidden bg-lpl-bg isolate min-h-svh flex"
            data-testid="hero-section"
        >
            <div className="relative w-full max-w-7xl mx-auto px-5 md:px-10 pt-2 md:pt-4 pb-12 md:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                {/* LEFT: Text */}
                <div className="lg:col-span-7 relative">
                    <h1
                        className="font-display font-semibold leading-[0.92] tracking-tight"
                        data-testid="hero-title"
                        style={{ color: "#0F2A20" }}
                    >
                        <motion.span
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.08 }}
                            className="block text-[clamp(3rem,8.5vw,7.2rem)]"
                        >
                            La Petite
                        </motion.span>
                        <motion.span
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.2 }}
                            className="block text-[clamp(3rem,8.5vw,7.2rem)]"
                            style={{ color: "#A3CFBB" }}
                        >
                            Lunetterie
                            <span className="font-serif italic font-normal" style={{ color: "#ECB6FF" }}>.</span>
                        </motion.span>
                    </h1>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        className="mt-10 max-w-xl font-body"
                    >
                        <div className="text-lg md:text-xl leading-snug text-lpl-text-muted">
                            <span>Ici, on vous offre </span>
                            <span
                                className="relative inline-grid overflow-hidden align-baseline leading-snug text-center"
                                data-testid="hero-rotating"
                            >
                                <span
                                    aria-hidden
                                    className="invisible col-start-1 row-start-1 font-serif italic whitespace-nowrap"
                                >
                                    des montures qui claquent
                                </span>
                                <AnimatePresence mode="wait">
                                    <motion.span
                                        key={index}
                                        initial={{ y: "100%", opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        exit={{ y: "-100%", opacity: 0 }}
                                        transition={{
                                            duration: 0.45,
                                            ease: [0.2, 0.8, 0.2, 1],
                                        }}
                                        className="col-start-1 row-start-1 font-serif italic whitespace-nowrap text-center"
                                        style={{ color: "#3D6E5B" }}
                                    >
                                        {ROTATING[index]}
                                    </motion.span>
                                </AnimatePresence>
                            </span>
                            <span> et des sourires garantis.</span>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.7 }}
                        className="mt-10 flex flex-wrap items-center gap-5"
                    >
                        <Link
                            to="/montures"
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center gap-3 bg-lpl-primary text-lpl-text px-7 py-4 rounded-full text-sm uppercase tracking-[0.16em] font-semibold hover:bg-lpl-text hover:text-lpl-bg transition-colors duration-300"
                        >
                            Voir les montures
                            <ArrowRight
                                size={18}
                                className="group-hover:translate-x-1 transition-transform"
                            />
                        </Link>
                        <a
                            href="#sylvie"
                            data-testid="hero-cta-secondary"
                            className="font-serif italic text-xl md:text-2xl underline decoration-2 decoration-lpl-yellow underline-offset-[6px] hover:text-lpl-primary-deep transition-colors"
                            style={{ color: "#3D6E5B" }}
                        >
                            ou rencontrer Sylvie
                        </a>
                    </motion.div>
                </div>

                {/* RIGHT: Image with terracotta panel behind */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.25 }}
                    className="lg:col-span-5 relative"
                >
                    <div className="relative w-full max-w-md mx-auto">
                        {/* Terracotta panel behind image */}
                        <div
                            aria-hidden
                            className="absolute -bottom-6 -right-6 w-full h-full"
                            style={{ background: "#DD7259" }}
                        />
                        {/* Yellow tiny square accent top-left */}
                        <div
                            aria-hidden
                            className="absolute -top-5 -left-5 w-14 h-14 rotate-[14deg]"
                            style={{ background: "#F2D580" }}
                        />
                        {/* Image */}
                        <img
                            src="/images/sylvie-apprentie-portrait.jpg"
                            alt="Sylvie et son apprentie à la lunetterie"
                            className="relative block w-full aspect-[4/5] object-cover shadow-xl"
                            data-testid="hero-image"
                        />

                        {/* Floating sticker: green "depuis 2012" */}
                        <div className="absolute -top-5 -right-5 md:-top-6 md:-right-8 rotate-[8deg] z-20">
                            <div
                                className="text-lpl-text px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.16em] font-bold shadow-lg flex items-center gap-1.5"
                                style={{ background: "#A3CFBB" }}
                            >
                                <Star size={13} fill="currentColor" />
                                Depuis 2018
                            </div>
                        </div>

                        {/* Floating sticker: pink circle "100% indé" */}
                        <div
                            className="absolute -bottom-10 -left-10 w-28 h-28 rounded-full flex items-center justify-center rotate-[-8deg] shadow-xl z-20"
                            style={{ background: "#ECB6FF" }}
                        >
                            <span
                                className="font-display text-lg text-center leading-[1.05]"
                                style={{ color: "#0F2A20" }}
                            >
                                100%
                                <br />
                                indé
                            </span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
